// 에어컨 청소 예약 — 홈페이지(gc-job.co.kr) 예약 목록 (2026-10-19)
//
// 대여 주문 목록(pages/rental/RentalOrderList.jsx)과 같은 구조: 헤더(마지막 동기화 · [동기화]) →
// 상태 탭(건수) → 카드. 카드 틀은 rental.css 클래스를 그대로 쓰고, 이 화면에만 있는 것은
// aircon.css(aircon- 접두어)에 둔다.
//
// ⚠️ 상태 변경은 항상 서버 API(PATCH /api/aircon/reservations/{id}/)로 보낸다. 서버가 홈페이지에
//    먼저 반영하고 성공해야 ERP 상태·캘린더를 바꾼다. 다음에 누를 수 있는 상태(next_statuses)도
//    서버가 준 값을 그대로 쓴다 — 프론트에 순서를 또 적으면 규칙이 두 곳으로 갈라진다.
// ⚠️ 담당자 변경도 같은 PATCH({assignee})다. 서버가 캘린더 일정의 담당자(직원 열)까지 옮긴다.
//    드롭다운 명단은 서버(assignees/ — .env 풀 설정, 없으면 활성 사용자 전체)가 정한다.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import api from "../../api";
import { formatDateShort, formatDateTime, formatSyncedAt } from "../rental/format";
import "../rental/rental.css";
import "./aircon.css";

// 키는 서버 상태 값(AirconStatus)과 같다. 기본 선택은 접수+확정.
export const STATUSES = ["접수", "확정", "완료", "취소"];
export const DEFAULT_STATUSES = ["접수", "확정"];
const STATUS_SLUG = { 접수: "received", 확정: "confirmed", 완료: "done", 취소: "canceled" };

const BUTTON_LABEL = { 확정: "확정", 완료: "완료 처리", 취소: "취소" };

function timeLabel(event) {
  if (!event?.start_time) return "";
  return `${String(event.start_time).slice(0, 5)}~${String(event.end_time || "").slice(0, 5)}`;
}

export default function AirconReservationList() {
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState(DEFAULT_STATUSES);
  const [assigneeFilter, setAssigneeFilter] = useState("");   // "" = 전체, "none" = 미배정, 그 외 사용자 id
  const [assignees, setAssignees] = useState([]);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { status: statuses.join(",") };
      if (assigneeFilter) params.assignee = assigneeFilter;
      const res = await api.get("aircon/reservations/", { params });
      setRows(res.data?.results || []);
      setCounts(res.data?.counts || {});
      setLastSyncedAt(res.data?.last_synced_at || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [statuses, assigneeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("aircon/reservations/assignees/")
      .then((res) => setAssignees(res.data?.results || []))
      .catch(() => setAssignees([]));
  }, []);

  const toggleStatus = (value) => {
    setStatuses((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : STATUSES.filter((s) => prev.includes(s) || s === value)
    );
  };

  const handleSync = async () => {
    setSyncing(true);
    setError("");
    try {
      const res = await api.post("aircon/reservations/sync/", {});
      setLastSyncedAt(res.data?.last_synced_at || null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "동기화에 실패했습니다.");
    } finally {
      setSyncing(false);
    }
  };

  const handleStatus = async (row, next) => {
    if (next === "취소" && !window.confirm(`${row.name || "이 예약"} 예약을 취소할까요? 홈페이지에도 취소로 반영되고 캘린더 일정이 지워집니다.`)) {
      return;
    }
    setBusyId(row.id);
    setError("");
    try {
      await api.patch(`aircon/reservations/${row.id}/`, { status: next });
      await load();
    } catch (e) {
      // 실패해도 다시 불러온다(홈페이지 반영 오류가 카드에 남는다). load()가 오류 문구를
      // 비우므로 문구는 그 뒤에 띄운다.
      await load();
      setError(e?.response?.data?.detail || "상태를 바꾸지 못했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  const handleAssignee = async (row, value) => {
    if (!value || String(row.assignee) === value) return;
    setBusyId(row.id);
    setError("");
    try {
      const res = await api.patch(`aircon/reservations/${row.id}/`, { assignee: Number(value) });
      const updated = res.data?.reservation;
      // 담당자 필터가 걸려 있으면 다른 사람에게 넘긴 카드는 목록에서 빠져야 하므로 다시 부른다.
      if (assigneeFilter || !updated) await load();
      else setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    } catch (e) {
      setError(e?.response?.data?.detail || "담당자를 바꾸지 못했습니다.");
    } finally {
      setBusyId(null);
    }
  };

  // 드롭다운 선택지 — 명단에 없는 현재 담당자(풀에서 빠진 사람 등)도 보이게 붙인다.
  const optionsFor = (row) => {
    const list = assignees.map((u) => ({ id: u.id, display: u.display }));
    if (row.assignee_info && !list.some((u) => u.id === row.assignee_info.id)) {
      list.push({ id: row.assignee_info.id, display: row.assignee_info.display });
    }
    return list;
  };

  // 캘린더로 이동 — 그 일정의 날짜로 가서 강조한다(CalendarPcLayout이 location.state를 읽는다).
  const openCalendar = (event) => {
    navigate("/etc/calendar/general", { state: { calendarFocus: event } });
  };

  return (
    <div className="rental-page">
      <div className="rental-header">
        <h2 className="rental-title">에어컨 청소 예약</h2>
        <div className="rental-header-right">
          <span className="rental-synced">{formatSyncedAt(lastSyncedAt)}</span>
          <button type="button" className="rental-btn-sync" onClick={handleSync} disabled={syncing}>
            {syncing ? "동기화 중" : "동기화"}
          </button>
        </div>
      </div>

      <div className="rental-tabs">
        {STATUSES.map((value) => (
          <button
            key={value}
            type="button"
            className={`rental-tab${statuses.includes(value) ? " rental-tab--active" : ""}`}
            onClick={() => toggleStatus(value)}
          >
            {value}
            <span className="rental-tab-count">{counts[value] ?? 0}</span>
          </button>
        ))}
        <select
          className="aircon-assignee-filter"
          aria-label="담당자 필터"
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
        >
          <option value="">전체 담당자</option>
          {assignees.map((u) => (
            <option key={u.id} value={String(u.id)}>{u.display}</option>
          ))}
          <option value="none">미배정</option>
        </select>
      </div>

      {error && <div className="rental-error">{error}</div>}

      {loading ? (
        <div className="rental-empty">불러오는 중…</div>
      ) : rows.length === 0 ? (
        <div className="rental-empty">해당하는 예약이 없습니다.</div>
      ) : (
        <div className="rental-cards">
          {rows.map((row) => (
            <div className="rental-card aircon-card" key={row.id} data-testid="aircon-card">
              <div className="rental-card-top">
                <div className="rental-card-names">
                  <span className="rental-card-name">{row.name || "-"}</span>
                  <a className="aircon-phone" href={`tel:${row.phone}`}>{row.phone}</a>
                </div>
                <span className={`aircon-badge aircon-badge--${STATUS_SLUG[row.status] || "received"}`}>
                  {row.status}
                </span>
              </div>

              <div className="rental-card-addr">{row.address || "주소 없음"}</div>

              <dl className="aircon-fields">
                <div className="aircon-field aircon-field--assignee">
                  <dt>담당자</dt>
                  <dd>
                    <select
                      className="aircon-assignee-select"
                      aria-label="담당자"
                      value={row.assignee ? String(row.assignee) : ""}
                      disabled={busyId === row.id}
                      onChange={(e) => handleAssignee(row, e.target.value)}
                    >
                      {!row.assignee && <option value="">미배정</option>}
                      {optionsFor(row).map((u) => (
                        <option key={u.id} value={String(u.id)}>{u.display}</option>
                      ))}
                    </select>
                  </dd>
                </div>
                <div className="aircon-field"><dt>접수</dt><dd>{formatDateTime(row.remote_created_at)}</dd></div>
                <div className="aircon-field"><dt>에어컨</dt><dd>{row.unit_summary || "-"}</dd></div>
                <div className="aircon-field"><dt>청소 범위</dt><dd>{row.clean_type || "-"}</dd></div>
                <div className="aircon-field">
                  <dt>희망일</dt>
                  <dd>
                    1지망 {formatDateShort(row.date1)}
                    {row.date2 ? ` · 2지망 ${formatDateShort(row.date2)}` : ""}
                    {row.time_pref ? ` · ${row.time_pref}` : ""}
                  </dd>
                </div>
                <div className="aircon-field"><dt>설치 높이</dt><dd>{row.height || "-"}</dd></div>
                <div className="aircon-field"><dt>주차</dt><dd>{row.parking || "-"}</dd></div>
                {row.memo && (
                  <div className="aircon-field aircon-field--memo"><dt>요청사항</dt><dd>{row.memo}</dd></div>
                )}
                <div className="aircon-field">
                  <dt>캘린더</dt>
                  <dd>
                    {row.calendar_event ? (
                      <button type="button" className="aircon-cal-link" onClick={() => openCalendar(row.calendar_event)}>
                        {formatDateShort(row.calendar_event.start_date)} {timeLabel(row.calendar_event)} 일정 보기
                      </button>
                    ) : (
                      <span className="aircon-muted">일정 없음</span>
                    )}
                  </dd>
                </div>
              </dl>

              {row.photo_urls?.length > 0 && (
                <div className="aircon-photos">
                  {row.photo_urls.map((url, index) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer" className="aircon-thumb-link">
                      <img src={url} alt={`사진 ${index + 1}`} className="aircon-thumb" />
                    </a>
                  ))}
                </div>
              )}

              {row.site_error && <div className="aircon-site-error">홈페이지 반영 실패: {row.site_error}</div>}

              {row.next_statuses?.length > 0 && (
                <div className="aircon-actions">
                  {row.next_statuses.map((next) => (
                    <button
                      key={next}
                      type="button"
                      className={`aircon-btn aircon-btn--${STATUS_SLUG[next]}`}
                      disabled={busyId === row.id}
                      onClick={() => handleStatus(row, next)}
                    >
                      {busyId === row.id ? "처리 중" : BUTTON_LABEL[next]}
                    </button>
                  ))}
                </div>
              )}
              <div className="aircon-remote-id">홈페이지 예약번호 {row.remote_id}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
